<template>
  <div class="tools">
    <el-container>
      <el-header height="60px">
        <h2>工具列表</h2>
        <p>浏览所有可用工具</p>
      </el-header>
      <el-main>
        <el-row :gutter="20">
          <el-col :xs="24" :md="18">
            <el-input
              v-model="searchQuery"
              placeholder="搜索工具名称或描述"
              clearable
              style="margin-bottom: 20px;"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
          </el-col>
          <el-col :xs="24" :md="6">
            <el-select
              v-model="selectedCategory"
              placeholder="选择分类"
              clearable
              style="width: 100%;"
            >
              <el-option
                v-for="cat in categoryOptions"
                :key="cat.id"
                :label="cat.name"
                :value="cat.id"
              />
            </el-select>
          </el-col>
        </el-row>

        <el-row :gutter="20" v-loading="loading">
          <el-col :xs="24" :sm="12" :md="8" v-for="tool in filteredTools" :key="tool.id">
            <el-card class="tool-card">
              <template #header>
                <div class="card-header">
                  <el-icon><Tools /></el-icon>
                  <span>{{ tool.name }}</span>
                </div>
              </template>
              <p class="description">{{ tool.description || '暂无描述' }}</p>
              <div class="meta">
                <el-tag size="small">{{ tool.category_name || '未分类' }}</el-tag>
                <el-tag size="small" :type="tool.tool_type === 'external' ? 'warning' : 'success'">
                  {{ tool.tool_type === 'external' ? '外链' : '自建' }}
                </el-tag>
                <span
                  v-if="tool.tool_type === 'external' && tool.url"
                  class="ext-domain"
                  :title="tool.url"
                >
                  <el-icon><Link /></el-icon>{{ getDomain(tool.url) }}
                </span>
              </div>
              <div class="actions">
                <el-button type="primary" @click="openTool(tool)">
                  {{ tool.tool_type === 'external' ? '打开' : '使用' }}
                </el-button>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <el-empty
          v-if="!loading && filteredTools.length === 0"
          :description="hasFilter ? '未找到符合条件的工具' : '暂无工具'"
        >
          <el-button v-if="hasFilter" type="primary" plain @click="clearFilters">
            清除筛选
          </el-button>
        </el-empty>
      </el-main>
    </el-container>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Search, Tools, Link } from '@element-plus/icons-vue'
import { getTools } from '@/api/tools'
import { getCategories } from '@/api/categories'
import type { Tool } from '@/types/tool'
import type { CategoryListItem } from '@/types/category'

const route = useRoute()
const router = useRouter()

const tools = ref<Tool[]>([])
const categories = ref<CategoryListItem[]>([])
const searchQuery = ref('')
const selectedCategory = ref<number | ''>('')
const loading = ref(false)

const categoryOptions = computed(() => [
  { id: '' as number | '', name: '全部' },
  ...categories.value
])

const hasFilter = computed(() => !!searchQuery.value || selectedCategory.value !== '')

const filteredTools = computed(() => {
  let filtered = tools.value
  if (selectedCategory.value) {
    filtered = filtered.filter((t) => t.category_id === selectedCategory.value)
  }
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    filtered = filtered.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description && t.description.toLowerCase().includes(q))
    )
  }
  return filtered
})

/** 从 URL 初始化筛选：?category= 分类筛选、?q= 搜索词（首页/分类页跳转携带）。 */
function syncFromQuery() {
  const c = route.query.category
  if (c !== undefined) {
    const id = Number(c)
    selectedCategory.value = c !== '' && !Number.isNaN(id) ? id : ''
  }
  const kw = route.query.q
  if (typeof kw === 'string') {
    searchQuery.value = kw
  }
}

// 同页跳转（如 /tools?category=2 → /tools?category=3）时保持筛选同步
watch(() => route.fullPath, syncFromQuery)

// 筛选变化同步回 URL，便于分享链接与刷新后保持状态（replace 不产生历史记录）
watch(selectedCategory, (val) => {
  if (route.name !== 'tools') return
  router.replace({
    query: { ...route.query, category: val === '' ? undefined : String(val) }
  })
})

onMounted(async () => {
  syncFromQuery()
  loading.value = true
  try {
    await Promise.all([loadCategories(), loadTools()])
  } finally {
    loading.value = false
  }
})

async function loadCategories() {
  try {
    categories.value = await getCategories()
  } catch (error) {
    console.error('Failed to load categories:', error)
  }
}

async function loadTools() {
  try {
    tools.value = await getTools()
  } catch (error) {
    console.error('Failed to load tools:', error)
    tools.value = []
  }
}

function clearFilters() {
  searchQuery.value = ''
  selectedCategory.value = ''
}

/** 提取外链域名，让用户在点击前知道要跳去哪个站点。 */
function getDomain(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

function openTool(tool: Tool) {
  if (tool.tool_type === 'external' && tool.url) {
    window.open(tool.url, '_blank', 'noopener')
  } else if (tool.internal_path) {
    router.push(tool.internal_path)
  } else {
    ElMessage.info('该工具暂未上线，敬请期待')
  }
}
</script>

<style scoped>
.tools {
  padding: 20px;
}
.tool-card {
  margin-bottom: 20px;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.25s, transform 0.25s;
}
.tool-card:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}
.tool-card :deep(.el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.description {
  color: #666;
  font-size: 14px;
  margin-bottom: 10px;
  /* 多行截断：最多 3 行 */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 42px;
}
.meta {
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.ext-domain {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: #909399;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.actions {
  margin-top: 15px;
  display: flex;
  gap: 10px;
}
.actions .el-button {
  flex: 1;
}
@media (max-width: 768px) {
  .tool-card {
    min-height: 0;
  }
}
</style>
